<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { BcIcon } from 'xangle-ui'

import EpIcon from '~/components/icon/EpIcon.vue'
import { SOCIAL_SHARE } from '~/constants/social-share.constants'

const emits = defineEmits([ 'update:showShareModal' ])

const route = useRoute()
const shareUrl = computed(() => import.meta.env.VITE_APP_BASE_URL + route.fullPath)

const closeModal = () => {
  emits('update:showShareModal', false)
}

const copy = async (value: string) => {
  await window.navigator.clipboard.writeText(value)
}

const isCopied = ref(false)
const modalRef = ref<HTMLDivElement | null>(null)

const copyShareUrl = async () => {
  await copy(shareUrl.value)
  isCopied.value = true
}

const share = (socialShareUrl: string) => {
  window.open(`${socialShareUrl}${encodeURIComponent(shareUrl.value)}`)
  closeModal()
}

watchEffect(() => {
  if (isCopied.value) {
    setTimeout(() => {
      isCopied.value = false
    }, 2000)
  }
})

onClickOutside(modalRef, () => {
  closeModal()
})
</script>

<template>
  <div
    class="modal-wrapper"
    ref="modalRef"
  >
    <div class="flex w-full items-center justify-between text-primary">
      <span class="font-bold">Share it</span>
      <button @click="closeModal">
        <EpIcon
          name="clear"
          :size="20"
        />
      </button>
    </div>
    <ul class="flex gap-x-2">
      <li
        v-for="[key, value] of Object.entries(SOCIAL_SHARE)"
        :key="key"
      >
        <button
          class="flex w-[58px] flex-col items-center justify-center bg-default/5 p-1"
          @click="share(value.shareUrl)"
        >
          <BcIcon
            :icon="value.icon"
            color="white"
            size="20px"
            class="m-2"
          />
          <span class="mt-1 text-xs text-default/40">
            {{ value.name }}
          </span>
        </button>
      </li>
    </ul>
    <div class="copy-wrapper">
      <input
        type="text"
        :value="shareUrl"
        class="url-input"
        readonly
      >
      <button
        class="copy-button"
        @click="copyShareUrl"
      >
        Copy
      </button>
    </div>
    <div
      v-if="isCopied"
      class="copied-message"
    >
      Copied
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal-wrapper {
  @apply absolute top-full right-0 z-[100] w-fit flex flex-col items-center gap-y-2 border border-default/10 bg-black p-4 shadow-default/20 shadow-[0px_16px_32px];

  .copy-wrapper {
    @apply flex justify-center gap-x-1 w-full;

    .url-input {
      @apply bg-transparent border border-default/10 w-full outline-none p-2;
    }

    .copy-button {
      @apply flex justify-center items-center bg-primary p-2;
    }
  }

  .copied-message {
    @apply flex justify-center items-center text-xs;
  }
}
</style>